import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { t, language } = useLanguage();
  const { token, refreshUser, setAuthFromToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [transactionInfo, setTransactionInfo] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loginUrl, setLoginUrl] = useState('https://osintlab.it/it/auth/login');

  const sessionId = searchParams.get('session_id');
  const isFree = searchParams.get('free') === 'true';
  // Phase-2 flow: pending_id present means we need to poll until webhook creates the user
  const pendingIdParam = searchParams.get('pending_id') || localStorage.getItem('bond_pending_id');

  useEffect(() => {
    fetchSettings();

    if (isFree) {
      setStatus('success');
    } else if (pendingIdParam) {
      // Phase-2: poll pending-status until webhook completes
      pollPendingStatus(pendingIdParam);
    } else if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId && !isFree) {
      setStatus('error');
    }
    // eslint-disable-next-line
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      if (response.data.osintlab_login_url) {
        setLoginUrl(response.data.osintlab_login_url);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const pollPendingStatus = async (pendingId, attempts = 0) => {
    const maxAttempts = 30; // 30 × 2s = 1 minute
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/pending-status/${pendingId}`);
      const data = response.data;
      if (data.status === 'completed') {
        setTransactionInfo(data.transaction);
        // Auto-login the new user with the issued token
        if (data.token && setAuthFromToken) {
          await setAuthFromToken(data.token);
        }
        localStorage.removeItem('bond_pending_id');
        localStorage.removeItem('bond_pending_token');
        setStatus('success');
        return;
      }
      if (data.status === 'rejected') {
        setRejectionReason(data.reason || '');
        setStatus('rejected');
        return;
      }
      if (data.status === 'expired') {
        setStatus('error');
        return;
      }
      setTimeout(() => pollPendingStatus(pendingId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('pending-status poll error:', error);
      setTimeout(() => pollPendingStatus(pendingId, attempts + 1), pollInterval);
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid' || response.data.status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      } else if (response.data.status === 'expired' || response.data.status === 'failed') {
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const isVerifyTrial = transactionInfo?.is_verify_charge && transactionInfo?.refunded;

  return (
    <div className="min-h-screen bg-smartBlack-DEFAULT flex flex-col">
      <Navbar onRegisterClick={() => {}} />
      
      <main className="flex-1 flex items-center justify-center pt-20 px-6">
        <div className="max-w-md w-full" data-testid="payment-success-page">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="animate-spin mx-auto text-smartRed" size={48} />
              <h1 className="font-heading font-bold text-2xl text-smartGrey-text mt-6">
                {language === 'it' ? 'Verifica pagamento...' : 'Verifying payment...'}
              </h1>
              <p className="text-smartGrey-muted mt-2">
                {language === 'it' ? 'Stiamo verificando il tuo pagamento...' : 'We are verifying your payment...'}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h1 className="font-heading font-bold text-3xl text-smartGrey-text mt-6">
                {isVerifyTrial
                  ? (language === 'it' ? 'Verifica completata!' : 'Verification completed!')
                  : (language === 'it' ? 'Ordine completato!' : 'Order completed!')}
              </h1>
              {isVerifyTrial ? (
                <p className="text-smartGrey-muted mt-2 mb-8">
                  {language === 'it'
                    ? <>Il tuo metodo di pagamento è stato verificato con successo. <strong className="text-[#D4AF37]">Abbiamo addebitato €1,00 e ti abbiamo già rimborsato l'importo</strong> (vedrai l'accredito sulla carta entro 2-7 giorni a seconda della banca).</>
                    : <>Your payment method has been verified. We charged €1.00 and immediately refunded it (the refund will appear on your card within 2-7 days depending on your bank).</>}
                </p>
              ) : (
                <p className="text-smartGrey-muted mt-2 mb-8">
                  {language === 'it'
                    ? 'Il tuo abbonamento è stato attivato con successo. Riceverai una email di conferma con tutti i dettagli.'
                    : 'Your subscription has been activated successfully. You will receive a confirmation email with all details.'}
                </p>
              )}

              <div className="card-smart rounded-md p-6 text-left mb-6">
                <h2 className="font-heading font-semibold text-lg text-smartGrey-text mb-4">
                  {language === 'it' ? 'Prossimi passi' : 'Next steps'}
                </h2>
                <ul className="space-y-3 text-sm text-smartGrey-muted">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-smartRed/20 rounded-full flex items-center justify-center flex-shrink-0 text-smartRed text-xs font-bold">1</span>
                    <span>{language === 'it' ? 'Controlla la tua email per la conferma dell\'ordine' : 'Check your email for order confirmation'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-smartRed/20 rounded-full flex items-center justify-center flex-shrink-0 text-smartRed text-xs font-bold">2</span>
                    <span>{language === 'it' ? 'Riceverai le credenziali di accesso alla piattaforma' : 'You will receive platform access credentials'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-smartRed/20 rounded-full flex items-center justify-center flex-shrink-0 text-smartRed text-xs font-bold">3</span>
                    <span>{language === 'it' ? 'Accedi alla piattaforma e inizia ad usare BOND' : 'Access the platform and start using BOND'}</span>
                  </li>
                </ul>
              </div>

              <a 
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="bond-btn bond-btn-primary w-full">
                  <ExternalLink size={18} />
                  {language === 'it' ? 'ACCEDI ALLA PIATTAFORMA' : 'ACCESS PLATFORM'}
                </Button>
              </a>

              <button 
                onClick={() => navigate('/')}
                className="text-smartGrey-muted hover:text-white text-sm mt-4 transition-colors"
              >
                {language === 'it' ? 'Torna alla home' : 'Back to home'}
              </button>
            </div>
          )}

          {status === 'rejected' && (
            <div className="text-center" data-testid="rejected-state">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="text-yellow-500" size={40} />
              </div>
              <h1 className="font-heading font-bold text-2xl text-smartGrey-text mt-6">
                {language === 'it' ? 'Registrazione non completata' : 'Registration not completed'}
              </h1>
              <p className="text-smartGrey-muted mt-2 mb-6">
                {rejectionReason}
              </p>
              <p className="text-xs text-smartGrey-muted mb-6">
                {language === 'it'
                  ? "L'eventuale addebito è stato già rimborsato automaticamente."
                  : "Any charge has already been refunded automatically."}
              </p>
              <Button onClick={() => navigate('/')} className="bond-btn bond-btn-secondary">
                {language === 'it' ? 'Torna alla home' : 'Back to home'}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="text-red-500" size={40} />
              </div>
              <h1 className="font-heading font-bold text-2xl text-smartGrey-text mt-6">
                {language === 'it' ? 'Errore nel pagamento' : 'Payment error'}
              </h1>
              <p className="text-smartGrey-muted mt-2 mb-8">
                {language === 'it' 
                  ? 'Si è verificato un errore durante il pagamento. Riprova o contattaci per assistenza.'
                  : 'An error occurred during payment. Please try again or contact us for assistance.'}
              </p>

              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/subscribe')}
                  variant="outline"
                  className="bond-btn bond-btn-secondary flex-1"
                >
                  {language === 'it' ? 'Riprova' : 'Try again'}
                </Button>
                <Button 
                  onClick={() => navigate('/#contact')}
                  className="bond-btn bond-btn-primary flex-1"
                >
                  {language === 'it' ? 'Contattaci' : 'Contact us'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
